import { createContext, useContext, useCallback, ReactNode, useMemo } from "react";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useIsAdminOrOwner } from "@/lib/rbac/hooks";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import {
  GetUserQuotasDocument,
  QuotaPeriodType,
  useGetUserQuotasQuery,
  useSetUserQuotasMutation,
} from "@/lib/graphql/types-and-hooks";
import {
  diffQuotaRows,
  quotaNodesToRows,
  quotaWindowForYears,
  type QuotaPeriod,
  type QuotaRow,
} from "@/lib/quotas/quota-mapping";

export type { QuotaPeriod, QuotaRow };

// v6: clean round-number seed (flat monthly amount per rep, no per-month
// wiggle) so the demo grid shows tidy values like $50,000 instead of
// $523,301. Bumping the suffix invalidates v5 caches that still hold jittered
// numbers.
const QUOTAS_STORAGE_SUFFIX = "quotas-v6";

/**
 * Build a stable period key for a given period type and reference date.
 */
export function buildPeriodKey(period: QuotaPeriod, date: Date): string {
  const y = date.getFullYear();
  if (period === "annual") return String(y);
  if (period === "quarterly") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `${y}-Q${q}`;
  }
  // monthly
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Human label for a period key.
 */
export function formatPeriodLabel(period: QuotaPeriod, periodKey: string): string {
  if (period === "annual") return periodKey;
  if (period === "quarterly") return periodKey; // "2026-Q1"
  // monthly: "2026-03" -> "Mar 2026"
  const [y, m] = periodKey.split("-");
  const monthIdx = parseInt(m, 10) - 1;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (monthIdx >= 0 && monthIdx < 12) return `${monthNames[monthIdx]} ${y}`;
  return periodKey;
}

/**
 * Generate a list of period keys around the current date for the period selector.
 * Returns 6 prior + current + 6 future.
 */
export function generatePeriodOptions(period: QuotaPeriod, anchor: Date = new Date()): string[] {
  const out: string[] = [];
  if (period === "annual") {
    const y = anchor.getFullYear();
    for (let i = -2; i <= 3; i++) out.push(String(y + i));
    return out;
  }
  if (period === "quarterly") {
    let y = anchor.getFullYear();
    let q = Math.floor(anchor.getMonth() / 3) + 1;
    // start 4 quarters back
    let yy = y, qq = q - 4;
    while (qq <= 0) { qq += 4; yy -= 1; }
    for (let i = 0; i < 8; i++) {
      out.push(`${yy}-Q${qq}`);
      qq += 1;
      if (qq > 4) { qq = 1; yy += 1; }
    }
    return out;
  }
  // monthly: 6 back, current, 6 forward
  const out2: string[] = [];
  for (let i = -6; i <= 6; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + i, 1);
    out2.push(buildPeriodKey("monthly", d));
  }
  return out2;
}

/**
 * Generate a list of years (as strings) for the year selector. Returns 2
 * prior + current + 3 future so admins can plan ahead.
 */
export function generateYearOptions(anchor: Date = new Date()): string[] {
  const y = anchor.getFullYear();
  const out: string[] = [];
  for (let i = -2; i <= 3; i++) out.push(String(y + i));
  return out;
}

/**
 * For a given period + selected year, return the list of period keys that
 * should appear as columns in the quota grid.
 *   • annual   → ["2026"]
 *   • quarterly→ ["2026-Q1", "2026-Q2", "2026-Q3", "2026-Q4"]
 *   • monthly  → ["2026-01", … "2026-12"]
 */
export function periodKeysForYear(period: QuotaPeriod, year: string): string[] {
  if (period === "annual") return [year];
  if (period === "quarterly") return [1, 2, 3, 4].map(q => `${year}-Q${q}`);
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
}

/**
 * Short column label for a period key, used by the quota table headers.
 *   • "2026"     → "2026"
 *   • "2026-Q1"  → "Q1"
 *   • "2026-03"  → "Mar"
 */
export function shortPeriodLabel(period: QuotaPeriod, periodKey: string): string {
  if (period === "annual") return periodKey;
  if (period === "quarterly") return periodKey.split("-")[1] ?? periodKey; // "Q1"
  const m = periodKey.split("-")[1];
  const monthIdx = parseInt(m, 10) - 1;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (monthIdx >= 0 && monthIdx < 12) return monthNames[monthIdx];
  return periodKey;
}

/**
 * Decompose a coarser periodKey into its underlying monthly keys.
 *   • "2026"    → ["2026-01" … "2026-12"]
 *   • "2026-Q1" → ["2026-01", "2026-02", "2026-03"]
 *   • "2026-03" → ["2026-03"]
 */
export function expandToMonthlyKeys(period: QuotaPeriod, periodKey: string): string[] {
  if (period === "monthly") return [periodKey];
  if (period === "quarterly") {
    const [y, qStr] = periodKey.split("-Q");
    const q = parseInt(qStr, 10);
    if (!q || q < 1 || q > 4) return [];
    const startMonth = (q - 1) * 3 + 1;
    return [0, 1, 2].map(i => `${y}-${String(startMonth + i).padStart(2, "0")}`);
  }
  // annual
  return Array.from({ length: 12 }, (_, i) => `${periodKey}-${String(i + 1).padStart(2, "0")}`);
}

/**
 * Decompose a coarser periodKey into its underlying quarterly keys (annual
 * only).
 */
export function expandToQuarterlyKeys(periodKey: string): string[] {
  return [1, 2, 3, 4].map(q => `${periodKey}-Q${q}`);
}

/**
 * Default seeded quotas so the demo workspace looks alive on first load.
 *
 * Design principles (April 2026 demo):
 *   1. Only reps (the 11 individual contributors) get stored rows. Pod managers
 *      (Lisa #2, Marcus #20, Priya #21) and the CRO (Doug #1) auto-roll up
 *      from direct reports — so quotas always add up exactly with no
 *      hand-tuned overrides.
 *   2. We seed every rep across the previous 3 months, current month, next 2
 *      months, the previous 2 quarters, current quarter, next quarter, and
 *      the current year. That guarantees no period the user can pick will
 *      ever show $0 quota.
 *   3. Per-rep quarterly numbers below; monthly = round(quarterly / 3),
 *      annual = quarterly * 4. Pod sums therefore always match the manager's
 *      effective quota; the CRO's effective quota = sum of all pods.
 */
const REP_QUARTERLY_QUOTAS: Record<string, number> = {
  // All quarterly quotas are multiples of 30,000 so monthly (÷3) and annual
  // (×4) values stay round (e.g. $50k / $150k / $600k). Keeps the demo grid
  // free of awkward "$523,301"-style numbers.
  // Lisa Park's pod (East) — pod total: 570k quarterly
  "3": 180000,   // Mike Chen — Senior AE      → 60k/mo · 720k/yr
  "22": 150000,  // Sarah Hopkins              → 50k/mo · 600k/yr
  "23": 150000,  // Jordan Reyes               → 50k/mo · 600k/yr
  "24": 90000,   // Emma Walsh                 → 30k/mo · 360k/yr
  // Marcus Lee's pod (West) — pod total: 540k quarterly
  "25": 180000,  // Tomás Rivera — Senior AE   → 60k/mo · 720k/yr
  "26": 150000,  // Aaliyah Brooks             → 50k/mo · 600k/yr
  "27": 120000,  // Henry Schmidt              → 40k/mo · 480k/yr
  "28": 90000,   // Sofia Greco — Associate    → 30k/mo · 360k/yr
  // Priya Shah's pod (Mid-Market) — pod total: 330k quarterly
  "29": 120000,  // Noah Patel                 → 40k/mo · 480k/yr
  "30": 120000,  // Maya Williams              → 40k/mo · 480k/yr
  "31": 90000,   // Kai Yamamoto — SMB         → 30k/mo · 360k/yr
  // Org rollup at CRO level: 1,440k quarterly · 5,760k annual
};

/**
 * Build the list of (period, periodKey) combinations we pre-seed.
 *
 * Only past + current periods are seeded — never future ones — so every
 * period the user can pick already has real closed-won deals attached and
 * will never display 0% attainment on first render.
 */
export function buildSeededPeriods(): Array<{ period: QuotaPeriod; periodKey: string }> {
  const now = new Date();
  const out: Array<{ period: QuotaPeriod; periodKey: string }> = [];

  // Monthly — last 3 + current
  for (let i = -3; i <= 0; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    out.push({ period: "monthly", periodKey: buildPeriodKey("monthly", d) });
  }

  // Quarterly — last 2 + current
  for (let i = -2; i <= 0; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i * 3, 1);
    out.push({ period: "quarterly", periodKey: buildPeriodKey("quarterly", d) });
  }

  // Annual — previous + current
  for (let i = -1; i <= 0; i++) {
    out.push({ period: "annual", periodKey: String(now.getFullYear() + i) });
  }

  return out;
}

/**
 * Map of rep userId → quarterly quota in workspace currency. Exported so the
 * companion attainment-deal generator (`dummyQuotaAttainment.ts`) can target
 * the same set of reps with realistic closed-won totals.
 */
export const REP_QUARTERLY_QUOTAS_MAP = REP_QUARTERLY_QUOTAS;

/**
 * Build the demo seed: monthly-only quotas for every rep across the previous
 * and current year (24 months). Quarterly + annual rows are derived on-the-
 * fly via horizontal rollup, so we never store redundant data.
 *
 * Each rep gets the same flat monthly amount every month so the demo numbers
 * stay clean and round (e.g. $50,000 / $600,000) instead of jittery values
 * like "$523,301" that look fake-precise.
 */
const DEFAULT_SEED: QuotaRow[] = (() => {
  const now = new Date();
  const years = [now.getFullYear() - 1, now.getFullYear()];
  const rows: QuotaRow[] = [];
  for (const [userId, quarterly] of Object.entries(REP_QUARTERLY_QUOTAS)) {
    const monthlyAmount = Math.round(quarterly / 3);
    for (const year of years) {
      for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
        const periodKey = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
        rows.push({ userId, period: "monthly", periodKey, amount: monthlyAmount });
      }
    }
  }
  return rows;
})();

interface QuotasContextType {
  rows: QuotaRow[];
  /** Get the quota row for a user/period/key combo (undefined if not set). */
  getQuota: (userId: string, period: QuotaPeriod, periodKey: string) => QuotaRow | undefined;
  /**
   * Upsert a quota for a user/period/key. Coarse periods are distributed across
   * their months — see `applyDistributedQuota`. Pass amount=0 to zero a cell;
   * use `clearQuota` to remove it entirely.
   */
  setQuota: (userId: string, period: QuotaPeriod, periodKey: string, amount: number) => void;
  /**
   * Apply many quotas as one write.
   *
   * Calling `setQuota` in a loop does NOT work: every call derives its next row
   * set from the `rows` captured at render, so each one discards the previous
   * iteration and only the last entry survives. That silently truncated CSV
   * imports to a single row. Folding the entries first is also what keeps a
   * production import to one `setUserQuotas` round trip.
   */
  setQuotasBulk: (entries: Array<{ userId: string; period: QuotaPeriod; periodKey: string; amount: number }>) => void;
  /** Remove a quota (so manager rollups become auto-sum again). */
  clearQuota: (userId: string, period: QuotaPeriod, periodKey: string) => void;
  /**
   * Get the effective quota for a user/period/key with horizontal rollup:
   *   • monthly   → stored value, or 0
   *   • quarterly → sum of stored monthly children if any exist, else stored
   *   • annual    → sum of stored monthly children if any exist, else sum of
   *                 stored quarterly children if any exist, else stored
   * `source` indicates how the value was derived so callers can show a
   * "rollup" badge and decide whether to allow editing.
   */
  getEffectiveQuota: (
    userId: string,
    period: QuotaPeriod,
    periodKey: string,
  ) => { amount: number; source: "stored" | "monthly-rollup" | "quarterly-rollup" | "empty" };
  /**
   * Set a quota for a (userId, period, periodKey). For coarser-grain edits
   * (quarterly / annual) the value is distributed evenly across the
   * underlying months so all rollups (cell-level + summary cards) stay
   * consistent and reflect the new total.
   *
   *   • monthly   → just stores the row.
   *   • quarterly → splits amount across 3 months (last month picks up the
   *                 remainder so the sum is exact). Clears any stored
   *                 quarterly row.
   *   • annual    → splits amount across 12 months (last month picks up the
   *                 remainder). Clears any stored quarterly + annual rows.
   *
   * Pass amount=0 (or call clearQuotaDistributed) to wipe the underlying
   * monthly rows for the period.
   */
  setQuotaDistributed: (userId: string, period: QuotaPeriod, periodKey: string, amount: number) => void;
  /** Clear all monthly rows underneath a (period, periodKey) for one user. */
  clearQuotaDistributed: (userId: string, period: QuotaPeriod, periodKey: string) => void;
  /**
   * Whether the current user may edit quotas.
   *
   * `setUserQuotas` is `@require_workspace_admin` — deliberately stricter than
   * the usual data-write check, so a rep cannot lower their own target. The UI
   * must hide the edit affordances rather than let a save 403.
   */
  canEditQuotas: boolean;
}

const QuotasContext = createContext<QuotasContextType | undefined>(undefined);

/**
 * Years the production query covers.
 *
 * Matches `generateYearOptions()` — the years the grid's year selector offers —
 * so every column the user can navigate to is already loaded and switching
 * years costs no round trip.
 */
function quotaQueryYears(): number[] {
  return generateYearOptions().map(Number);
}

/**
 * Apply a quota to `(userId, period, periodKey)`, always landing as monthly rows.
 *
 * A coarse amount is split evenly across the months it covers, with the last
 * month absorbing the remainder so the sum is exact. Any stored coarse row at or
 * below the edited level is dropped, since `getEffectiveQuota` prefers the
 * monthly rollup and would ignore it anyway.
 *
 * Monthly-only storage is what the production read depends on: the grid loads
 * one MONTH-typed window and derives quarters and years from it.
 */
function applyDistributedQuota(
  rows: QuotaRow[],
  userId: string,
  period: QuotaPeriod,
  periodKey: string,
  amount: number,
): QuotaRow[] {
  const cleanAmount = Math.max(0, Math.round(amount));

  if (period === "monthly") {
    const idx = rows.findIndex(r => r.userId === userId && r.period === "monthly" && r.periodKey === periodKey);
    const next = [...rows];
    const row: QuotaRow = { userId, period: "monthly", periodKey, amount: cleanAmount };
    if (idx >= 0) next[idx] = row;
    else next.push(row);
    return next;
  }

  const monthKeys = expandToMonthlyKeys(period, periodKey);
  const n = monthKeys.length;
  const per = Math.floor(cleanAmount / n);
  const remainder = cleanAmount - per * n;

  const next = rows.filter(r => {
    if (r.userId !== userId) return true;
    if (r.period === "monthly" && monthKeys.includes(r.periodKey)) return false;
    if (r.period === period && r.periodKey === periodKey) return false;
    if (period === "annual" && r.period === "quarterly" && expandToQuarterlyKeys(periodKey).includes(r.periodKey)) return false;
    return true;
  });
  monthKeys.forEach((mk, i) => {
    next.push({ userId, period: "monthly", periodKey: mk, amount: i === n - 1 ? per + remainder : per });
  });
  return next;
}

export function QuotasProvider({ children }: { children: ReactNode }) {
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const isAdmin = useIsAdminOrOwner();

  // Preview keeps the seeded localStorage store so the demo grid stays alive and
  // the UI stays designable without a backend.
  const [localRows, setLocalRows] = useWorkspaceLocalStorage<QuotaRow[]>(QUOTAS_STORAGE_SUFFIX, DEFAULT_SEED);

  // Production reads from `userQuotas` (coffee #4146, V185 user_quota).
  //
  // MONTH only, deliberately: the grid stores monthly rows and derives quarters
  // and years from them by rollup (see `getEffectiveQuota`), exactly as it did
  // in localStorage. The backend also accepts QUARTER and YEAR rows, but this
  // screen never writes them — every write path funnels through the monthly
  // distribution below — so one query backs every column.
  const quotaWindow = useMemo(() => quotaWindowForYears(quotaQueryYears()), []);
  const queryVariables = useMemo(
    () => ({
      workspaceId: workspaceId ?? "",
      periodType: QuotaPeriodType.Month,
      from: quotaWindow?.from ?? "",
      to: quotaWindow?.to ?? "",
    }),
    [workspaceId, quotaWindow],
  );

  // QuotasProvider wraps the whole app (App.tsx), so an ungated query here would
  // be one extra request on every page load for every user, whether or not the
  // workspace uses quotas at all. The flag already gates every quota surface --
  // the Deals Quota tab and the Settings screen -- so nothing that reads this
  // context is reachable while it is off.
  const quotasEnabled = useFeatureFlag(FEATURE_FLAGS.QUOTAS);

  const { data } = useGetUserQuotasQuery({
    skip: preview || !quotasEnabled || !workspaceId || !quotaWindow,
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  const serverRows = useMemo(() => quotaNodesToRows(data?.userQuotas), [data?.userQuotas]);
  const rows = preview ? localRows : serverRows;

  const [setUserQuotas] = useSetUserQuotasMutation();

  /**
   * Single persistence seam.
   *
   * Every mutator below computes the whole next row set and hands it here, so
   * the only thing that differs between preview and production is where it
   * lands. In production the change is expressed as a diff, because
   * `setUserQuotas` is an upsert-or-delete over the cells you name rather than a
   * replace-everything write.
   */
  const persist = useCallback((next: QuotaRow[]) => {
    if (preview) {
      setLocalRows(next);
      return;
    }
    // Writes are @require_workspace_admin server-side — stricter than the usual
    // data-write check so a rep cannot lower their own target. The UI hides the
    // edit affordances, so reaching here without admin means something slipped
    // through; drop it rather than firing a mutation that will 403.
    if (!workspaceId || !isAdmin) return;

    const quotas = diffQuotaRows(rows, next);
    if (quotas.length === 0) return;

    // Deletions are the cells sent with a null amount; the mutation response
    // only carries the rows it upserted, so the removals have to come from the
    // request we just built.
    const deletedKeys = new Set(
      quotas.filter(q => q.amount == null).map(q => `${q.userId}::${q.periodStart}`),
    );

    void setUserQuotas({
      variables: { workspaceId, quotas },
      // Patch the cache from the mutation result rather than refetching --
      // `refetchQueries` on an update mutation is a project-wide DON'T. The
      // written rows come back with real ids, so this is a merge of known
      // values, not a hand-rolled guess at what the server did.
      update: (cache, { data: result }) => {
        const written = result?.setUserQuotas?.quotas ?? [];
        cache.updateQuery(
          { query: GetUserQuotasDocument, variables: queryVariables },
          existing => {
            const kept = (existing?.userQuotas ?? []).filter(
              q => !deletedKeys.has(`${q.userId}::${q.periodStart}`)
                && !written.some(w => w.userId === q.userId && w.periodStart === q.periodStart),
            );
            return { userQuotas: [...kept, ...written] };
          },
        );
      },
    });
  }, [preview, setLocalRows, workspaceId, isAdmin, rows, setUserQuotas, queryVariables]);

  const getQuota = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string) =>
      rows.find(r => r.userId === userId && r.period === period && r.periodKey === periodKey),
    [rows]
  );

  const setQuota = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string, amount: number) => {
      // Distributes rather than storing a coarse row. Two reasons, both
      // pre-existing: `getEffectiveQuota` already prefers the monthly rollup, so
      // a stored quarterly/annual row was ignored whenever any month was set;
      // and production reads a MONTH-typed window, so a coarse row would be
      // written and never read back. The only caller that passes a coarse
      // period is the CSV import, which now agrees with inline editing.
      persist(applyDistributedQuota(rows, userId, period, periodKey, amount));
    },
    [rows, persist]
  );

  const setQuotasBulk = useCallback(
    (entries: Array<{ userId: string; period: QuotaPeriod; periodKey: string; amount: number }>) => {
      if (entries.length === 0) return;
      const next = entries.reduce(
        (acc, e) => applyDistributedQuota(acc, e.userId, e.period, e.periodKey, e.amount),
        rows,
      );
      persist(next);
    },
    [rows, persist],
  );

  const clearQuota = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string) => {
      persist(rows.filter(r => !(r.userId === userId && r.period === period && r.periodKey === periodKey)));
    },
    [rows, persist]
  );

  const clearQuotaDistributed = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string) => {
      if (period === "monthly") {
        persist(rows.filter(r => !(r.userId === userId && r.period === "monthly" && r.periodKey === periodKey)));
        return;
      }
      const monthKeys = expandToMonthlyKeys(period, periodKey);
      const next = rows.filter(r => {
        if (r.userId !== userId) return true;
        if (r.period === "monthly" && monthKeys.includes(r.periodKey)) return false;
        // also drop any stored coarse override at this exact level
        if (r.period === period && r.periodKey === periodKey) return false;
        // and at intermediate coarser levels (annual edit also clears any quarterly overrides)
        if (period === "annual" && r.period === "quarterly" && expandToQuarterlyKeys(periodKey).includes(r.periodKey)) return false;
        return true;
      });
      persist(next);
    },
    [rows, persist],
  );

  const setQuotaDistributed = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string, amount: number) => {
      persist(applyDistributedQuota(rows, userId, period, periodKey, amount));
    },
    [rows, persist],
  );

  const getEffectiveQuota = useCallback(
    (userId: string, period: QuotaPeriod, periodKey: string) => {
      // Monthly: stored or 0
      if (period === "monthly") {
        const r = rows.find(x => x.userId === userId && x.period === "monthly" && x.periodKey === periodKey);
        if (r) return { amount: r.amount, source: "stored" as const };
        return { amount: 0, source: "empty" as const };
      }
      // Quarterly: prefer monthly rollup if any month is set
      if (period === "quarterly") {
        const monthKeys = expandToMonthlyKeys("quarterly", periodKey);
        const monthlyRows = rows.filter(x => x.userId === userId && x.period === "monthly" && monthKeys.includes(x.periodKey));
        if (monthlyRows.length > 0) {
          return { amount: monthlyRows.reduce((s, r) => s + r.amount, 0), source: "monthly-rollup" as const };
        }
        const stored = rows.find(x => x.userId === userId && x.period === "quarterly" && x.periodKey === periodKey);
        if (stored) return { amount: stored.amount, source: "stored" as const };
        return { amount: 0, source: "empty" as const };
      }
      // Annual: prefer monthly, then quarterly, then stored
      const monthKeys = expandToMonthlyKeys("annual", periodKey);
      const monthlyRows = rows.filter(x => x.userId === userId && x.period === "monthly" && monthKeys.includes(x.periodKey));
      if (monthlyRows.length > 0) {
        return { amount: monthlyRows.reduce((s, r) => s + r.amount, 0), source: "monthly-rollup" as const };
      }
      const quarterKeys = expandToQuarterlyKeys(periodKey);
      const quarterlyRows = rows.filter(x => x.userId === userId && x.period === "quarterly" && quarterKeys.includes(x.periodKey));
      if (quarterlyRows.length > 0) {
        return { amount: quarterlyRows.reduce((s, r) => s + r.amount, 0), source: "quarterly-rollup" as const };
      }
      const stored = rows.find(x => x.userId === userId && x.period === "annual" && x.periodKey === periodKey);
      if (stored) return { amount: stored.amount, source: "stored" as const };
      return { amount: 0, source: "empty" as const };
    },
    [rows],
  );

  // Preview has no backend to authorize against, so it stays fully editable.
  const canEditQuotas = preview || isAdmin;

  const value = useMemo(
    () => ({ rows, getQuota, setQuota, setQuotasBulk, clearQuota, getEffectiveQuota, setQuotaDistributed, clearQuotaDistributed, canEditQuotas }),
    [rows, getQuota, setQuota, setQuotasBulk, clearQuota, getEffectiveQuota, setQuotaDistributed, clearQuotaDistributed, canEditQuotas],
  );

  return <QuotasContext.Provider value={value}>{children}</QuotasContext.Provider>;
}

export function useQuotas(): QuotasContextType {
  const ctx = useContext(QuotasContext);
  if (!ctx) throw new Error("useQuotas must be used inside <QuotasProvider>");
  return ctx;
}